import { borderRadius, boxShadows, palette, spaces } from '@kimuno/theme';
import { Box, Button, IconButton, Menu, MenuItem, Stack, SvgIcon, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

import type { ConversationDocument } from '../../../reducers/aiAssistant.reducers';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import { getCleanInnerText } from '../../../utils/copyElementContent';
import { copyFormattedContent } from '../../../utils/copyFormattedContent';
import getMuiIcon from '../../../utils/getMuiIcon';
import { getMarkdownComponents, remarkBrTags } from '../../../utils/markdown';
import {
	extractImageWidthsFromMarkdown,
	normalizeBlockMathDelimiters,
	prepareDocumentMarkdownForChat,
} from '../../utils/contentConverter';

export const EXTENDED_DOCUMENT_CARD_TITLE_TEST_ID = 'extended-document-card-title';

interface ExtendedChatMessageArtifactCardProps {
	document: ConversationDocument;
	onEdit?: () => void;
	onDownloadPdf?: () => void;
	onDownloadDocx?: () => void;
	testId?: string;
}

// Expanded document card (document-only for now). Kept functionally unchanged
// from the former ExtendedDocumentCard; renamed alongside ChatMessageArtifactCard.
const ExtendedChatMessageArtifactCard = React.forwardRef<
	HTMLDivElement,
	ExtendedChatMessageArtifactCardProps
>(({ document, onEdit, onDownloadPdf, onDownloadDocx, testId = 'extended-document-card' }, ref) => {
	const { t } = useTranslation();
	const contentRef = useRef<HTMLDivElement>(null);
	const [showScrollButton, setShowScrollButton] = useState(false);
	const [isCopied, setIsCopied] = useState(false);
	const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
	const isStreaming = document.status === 'started';
	// Normalised first: the width offsets must refer to the string ReactMarkdown parses.
	const { cleanedMarkdown, widthsByOffset } = extractImageWidthsFromMarkdown(
		normalizeBlockMathDelimiters(prepareDocumentMarkdownForChat(document.content || ''))
	);

	const handleScroll = () => {
		if (!contentRef.current) return;
		const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
		// Show button if we're not at the bottom and content is scrollable
		const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
		const nextShowScrollButton = !isAtBottom && scrollHeight > clientHeight;
		// This runs on every streamed chunk. An unchanged value can still schedule a render, and
		// 50 stream commits in a row that leave one pending make React throw #185.
		if (nextShowScrollButton !== showScrollButton) setShowScrollButton(nextShowScrollButton);
	};

	const scrollToBottom = () => {
		if (contentRef.current) {
			contentRef.current.scrollTo({
				top: contentRef.current.scrollHeight,
				behavior: 'smooth',
			});
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		handleScroll();
	}, [document.content]);

	const handleCopyClick = async () => {
		try {
			if (!contentRef.current) {
				return;
			}
			const htmlContent = contentRef.current.innerHTML;
			const plainText = getCleanInnerText(contentRef.current);

			await copyFormattedContent(htmlContent, plainText);
			setIsCopied(true);
		} catch (error) {
			notifyBugsnagHandledError(error as Error);
		}
	};

	useEffect(() => {
		if (isCopied) {
			const timeout = setTimeout(() => {
				setIsCopied(false);
			}, 5000);
			return () => clearTimeout(timeout);
		}
		return undefined;
	}, [isCopied]);

	const markdownComponents = useMemo(() => getMarkdownComponents(widthsByOffset), [widthsByOffset]);

	return (
		<Box
			ref={ref}
			data-testid={testId}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				width: '100%',
				backgroundColor: palette.background.paperElevation2,
				border: `1px solid ${palette.elevation.outlined}`,
				borderRadius: borderRadius.md,
				overflow: 'hidden',
				position: 'relative',
				boxShadow: boxShadows.elevation1,
				maxHeight: '600px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: `${spaces.md} ${spaces.lg} ${spaces.xs} ${spaces.lg}`,
					backgroundColor: palette.background.paperElevation2,
					borderBottom: `1px solid ${palette.divider}`,
					position: 'sticky',
					top: 0,
					zIndex: 2,
				}}
			>
				<Typography
					data-testid={EXTENDED_DOCUMENT_CARD_TITLE_TEST_ID}
					variant="subtitle1"
					sx={{
						fontWeight: 500,
						color: palette.text.primary,
						flexGrow: 1,
					}}
				>
					{document.title || t('Document')}
				</Typography>

				<Stack direction="row" spacing={spaces.xs}>
					<Button
						data-testid="extended-document-card-copy-button"
						size="medium"
						variant="text"
						color="secondary"
						onClick={handleCopyClick}
						disabled={isStreaming}
						startIcon={
							<SvgIcon
								component={getMuiIcon(isCopied ? 'check' : 'copy')}
								sx={{
									color: isCopied
										? palette.success.main
										: isStreaming
											? palette.action.disabled
											: palette.secondary.main,
								}}
								aria-hidden="true"
							/>
						}
						sx={{
							color: palette.secondary.main,
							fontWeight: 500,
						}}
					>
						{t('Copy')}
					</Button>
					<Button
						data-testid="extended-document-card-edit-button"
						size="medium"
						variant="text"
						color="secondary"
						onClick={onEdit}
						startIcon={<SvgIcon component={getMuiIcon('edit')} />}
						sx={{ color: palette.secondary.main, fontWeight: 500 }}
					>
						{t('Edit')}
					</Button>
					<Button
						data-testid="extended-document-card-download-button"
						size="medium"
						variant="text"
						color="secondary"
						onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
						disabled={isStreaming}
						startIcon={<SvgIcon component={getMuiIcon('download')} />}
						aria-haspopup="true"
						sx={{ color: palette.secondary.main, fontWeight: 500 }}
					>
						{t('Download')}
					</Button>
					<Menu
						anchorEl={downloadMenuAnchor}
						open={Boolean(downloadMenuAnchor)}
						onClose={() => setDownloadMenuAnchor(null)}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						transformOrigin={{ vertical: 'top', horizontal: 'right' }}
						sx={{
							zIndex: 1500,
							'& .MuiMenu-paper': {
								borderRadius: borderRadius.md,
								boxShadow: boxShadows.elevation4,
								padding: `0 ${spaces.xs}`,
							},
						}}
					>
						<MenuItem
							data-testid="extended-document-card-download-pdf"
							onClick={() => {
								setDownloadMenuAnchor(null);
								onDownloadPdf?.();
							}}
							sx={{ borderRadius: borderRadius.sm }}
						>
							<SvgIcon
								component={getMuiIcon('downloadAsPdf')}
								sx={{
									fill: palette.text.primary,
									color: palette.text.primary,
									marginRight: spaces.md,
								}}
							/>
							<Typography>{t('Download as PDF')}</Typography>
						</MenuItem>
						<MenuItem
							data-testid="extended-document-card-download-docx"
							onClick={() => {
								setDownloadMenuAnchor(null);
								onDownloadDocx?.();
							}}
							sx={{ borderRadius: borderRadius.sm }}
						>
							<SvgIcon
								component={getMuiIcon('downloadAsDocx')}
								sx={{
									fill: palette.text.primary,
									color: palette.text.primary,
									marginRight: spaces.md,
								}}
							/>
							<Typography>{t('Download as DOCX')}</Typography>
						</MenuItem>
					</Menu>
				</Stack>
			</Box>
			<Box
				ref={contentRef}
				onScroll={handleScroll}
				sx={{
					padding: `${spaces.xs} ${spaces.lg} ${spaces.lg} ${spaces.lg}`,
					overflowY: 'auto',
					flexGrow: 1,
					'& p': {
						...getTypographyStyles('body1'),
						marginBottom: spaces.md,
					},
					// Custom scrollbar styling
					'&::-webkit-scrollbar': {
						width: '8px',
					},
					'&::-webkit-scrollbar-track': {
						background: 'transparent',
					},
					'&::-webkit-scrollbar-thumb': {
						background: alpha(palette.text.primary, 0.1),
						borderRadius: '4px',
					},
					'&::-webkit-scrollbar-thumb:hover': {
						background: alpha(palette.text.primary, 0.2),
					},
				}}
			>
				<ReactMarkdown
					remarkPlugins={[remarkGfm, remarkMath, remarkBrTags]}
					rehypePlugins={[rehypeKatex]}
					components={markdownComponents}
				>
					{cleanedMarkdown}
				</ReactMarkdown>
			</Box>
			{showScrollButton && (
				<IconButton
					onClick={scrollToBottom}
					data-testid="scroll-to-bottom-button"
					sx={{
						position: 'absolute',
						bottom: spaces.md,
						left: '50%',
						transform: 'translateX(-50%)',
						backgroundColor: palette.background.default,
						color: palette.text.primary,
						boxShadow: boxShadows.elevation6,
						borderRadius: borderRadius.full,
						padding: spaces.sm,
						width: '48px',
						height: '48px',
						'&:hover': {
							backgroundColor: alpha(palette.background.default, 0.8),
						},
						zIndex: 3,
						'& .MuiSvgIcon-root': {
							fontSize: '24px',
						},
					}}
				>
					<SvgIcon component={getMuiIcon('arrowDown')} data-testid="arrow-down-icon" />
				</IconButton>
			)}
		</Box>
	);
});

// Helper for typography styles if needed, or use variants
const getTypographyStyles = (variant: 'body1' | 'subtitle1') => {
	if (variant === 'body1') {
		return {
			fontFamily: 'Poppins, sans-serif',
			fontSize: '16px',
			lineHeight: '24px',
			fontWeight: 300,
		};
	}
	return {};
};

export default ExtendedChatMessageArtifactCard;
